import React, { useCallback, useEffect, useState } from "react";
import notifications from "../../../common/Components/Notifications/Notification";
import { getSareeNumbers, setSareeStart } from "../../../services/karkhanaApi";
import { useKT } from "../i18n/kt";
import { cleanSareeNumber } from "./sareeEditModel";

/**
 * The studio's saree number series: the next number a new saree gets, and (owner or manager) where the
 * series goes on from. It only moves forward; the server refuses a start at or below a number already used.
 */
const SareeNumberSeries = () => {
  const { t } = useKT();
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState("");
  const [start, setStart] = useState("");
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res: any = await getSareeNumbers();
      if (res?.success) {
        setInfo(res.data);
        setError("");
      } else setError(res?.message || "Could not load");
    } catch {
      setError("Could not reach the server");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setProblem("");
    try {
      const res: any = await setSareeStart(Number(start));
      if (!res?.success) {
        setProblem(res?.message || "Could not save");
        return;
      }
      setInfo(res.data);
      setStart("");
      notifications({ title: t("Saved"), message: t("The next saree gets number {{n}}", { n: res.data?.next }), type: "success" });
    } catch {
      setProblem("This needs a connection. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="kh-settings__group">
      <h3>{t("Saree numbers")}</h3>
      <div className="kh-since">
        {error ? <p className="kw-muted">{t(error)}</p> : null}
        {!info && !error ? <p className="kw-muted">{t("Loading…")}</p> : null}
        {info ? (
          <>
            <div className="kh-since__row">
              <span>
                {t("Next saree number")}: <strong className="kw-code">{info.next}</strong>
              </span>
              {info.highestUsed ? <span className="kw-muted">{t("highest used {{n}}", { n: info.highestUsed })}</span> : null}
            </div>
            <p className="kw-muted kh-since__hint">
              {t("New sarees always get a number after the highest ever given. A number is never given twice, even after a saree is renumbered.")}
            </p>
            {info.canSetStart ? (
              <>
                <label className="kr-label" htmlFor="kh-series-start">
                  {t("Go on from (e.g. 18350)")}
                </label>
                <input
                  id="kh-series-start"
                  className="kr-select"
                  inputMode="numeric"
                  value={start}
                  placeholder={String(info.next)}
                  onChange={(e) => { setStart(cleanSareeNumber(e.target.value)); setProblem(""); }}
                />
                {problem ? (
                  <p className="kh-since__problem" role="alert">
                    {t(problem)}
                  </p>
                ) : null}
                <div className="kh-since__actions">
                  <button type="button" className="kh-btn is-primary" disabled={busy || !start} onClick={save}>
                    {busy ? t("Saving…") : t("Set series start")}
                  </button>
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
};

export default SareeNumberSeries;
